// src/components/manager-v2/ManagerSidebar.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
    Home, User, Clock, Calendar, DollarSign, FileText,
    Users, CheckSquare, TrendingUp, FileBarChart,
    ChevronLeft, ChevronRight, UserPlus, ClipboardCheck, Briefcase, Award,
    CalendarCheck, ListTodo, GitBranch
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import approvalService from '../../services/approval.service';
import logo from '../../oakhr-logo-full.svg';
import logo2 from '../../oakhr-tree.svg';

const ManagerSidebar = ({ isOpen, onToggle, user }) => {
    const { theme } = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const [pendingCount, setPendingCount] = useState(0);
    const [pendingBreakdown, setPendingBreakdown] = useState({ leave: 0, attendance: 0, flexi: 0 });

    // Hover flyout state
    const [hoveredItem, setHoveredItem] = useState(null);
    const [flyoutPos, setFlyoutPos] = useState({ top: 0, left: 0 });
    const hoverTimeoutRef = useRef(null);

    // Fetch pending approvals count
    useEffect(() => {
        const fetchPendingCount = async () => {
            try {
                const response = await approvalService.getPendingApprovalsCount();
                if (response.success) {
                    setPendingCount(response.total || 0);
                    setPendingBreakdown({
                        leave: response.leave || 0,
                        attendance: response.attendance || 0,
                        flexi: response.other || 0,
                    });
                }
            } catch (error) {
                console.error('Error fetching pending approvals count:', error);
            }
        };

        fetchPendingCount();
        const interval = setInterval(fetchPendingCount, 60000);
        return () => clearInterval(interval);
    }, []);

    // Dashboard is standalone
    const dashboardItem = { path: '/manager/dashboard', icon: Home, label: 'Dashboard', description: 'Overview and insights', tourClass: 'tour-manager-dashboard' };

    const personalMenuItems = [
        { path: '/manager/profile', icon: User, label: 'My Profile', description: 'Personal profile' },
        { path: '/manager/onboarding', icon: UserPlus, label: 'My Onboarding', description: 'Onboarding tasks', badge: true },
        { path: '/manager/attendance', icon: Clock, label: 'My Attendance', description: 'Time tracking' },
        { path: '/manager/leaves', icon: Calendar, label: 'My Leaves', description: 'Leave requests' },
        { path: '/manager/performance', icon: Award, label: 'My Performance', description: 'Performance reviews' },
        { path: '/manager/payroll', icon: DollarSign, label: 'My Compensation', description: 'Salary information' },
        { path: '/manager/documents', icon: FileText, label: 'My Documents', description: 'Personal documents' },
        { path: '/manager/taskbox?view=team', icon: CheckSquare, label: 'My Tasks', description: 'Personal task management' },
    ];

    const teamMenuItems = [
        { path: '/manager/team-management', icon: Users, label: 'Team Management', description: 'Manage team members', tourClass: 'tour-manager-team-management' },
        { path: '/manager/team-attendance', icon: CalendarCheck, label: 'Team Attendance', description: 'Team attendance tracking', tourClass: 'tour-manager-team-attendance' },
        { path: '/manager/team-approvals', icon: ClipboardCheck, label: 'Team Approvals', description: 'Approve leave and other requests', tourClass: 'tour-manager-team-approvals', subItems: [
            { tab: 'leave', label: 'Leave', badgeKey: 'leave' },
            { tab: 'attendance', label: 'Attendance', badgeKey: 'attendance' },
            { tab: 'flexi', label: 'Flexi', badgeKey: 'flexi' },
        ]},
        { path: '/manager/team-performance', icon: TrendingUp, label: 'Team Performance', description: 'Track team performance', tourClass: 'tour-manager-team-performance', subItems: [
            { tab: 'performance', label: 'Performance' },
            { tab: 'self-reviews', label: 'Self Reviews' },
            { tab: 'analytics', label: 'Analytics' },
        ]},
        { path: '/manager/team-taskbox', icon: ListTodo, label: 'Team Taskbox', description: 'Create and manage team tasks', tourClass: 'tour-manager-team-taskbox' },
        { path: '/manager/team-reports', icon: FileBarChart, label: 'Team Reports', description: 'Generate team reports', tourClass: 'tour-manager-team-reports' },
        { path: '/manager/flows', icon: GitBranch, label: 'Team Automations', description: 'Workflow automations for your team', tourClass: 'tour-manager-flows' },
    ];

    const isActive = (path) => {
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };

    const currentTab = new URLSearchParams(location.search).get('tab');

    const handleMouseEnter = useCallback((path, e) => {
        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
        const rect = e.currentTarget.getBoundingClientRect();
        setFlyoutPos({ top: rect.top, left: rect.right + 4 });
        setHoveredItem(path);
    }, []);

    const handleMouseLeave = useCallback(() => {
        hoverTimeoutRef.current = setTimeout(() => {
            setHoveredItem(null);
        }, 150);
    }, []);

    const handleSubItemClick = useCallback((path, tab) => {
        setHoveredItem(null);
        navigate(`${path}?tab=${tab}`);
    }, [navigate]);

    const renderFlyout = (item) => {
        const hasSubItems = item.subItems && item.subItems.length > 0;
        const isHovered = hoveredItem === item.path;
        if (!hasSubItems || !isHovered) return null;

        return (
            <div
                className="fixed z-[9999]"
                style={{ top: flyoutPos.top - 8, left: flyoutPos.left }}
                onMouseEnter={() => { if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current); }}
                onMouseLeave={handleMouseLeave}
            >
                <div className={`rounded-xl shadow-xl border min-w-[190px] overflow-hidden divide-y ${
                    theme === 'dark'
                        ? 'bg-gray-800 border-gray-700 shadow-black/30 divide-gray-700'
                        : 'bg-white border-gray-200 shadow-gray-200/50 divide-gray-100'
                }`}>
                    {item.subItems.map((sub) => {
                        const isSubActive = isActive(item.path) && currentTab === sub.tab;
                        return (
                            <button
                                key={sub.tab}
                                onClick={() => handleSubItemClick(item.path, sub.tab)}
                                className={`w-full text-left px-3 py-1.5 text-xs font-medium transition-colors flex items-center justify-between ${
                                    isSubActive
                                        ? theme === 'dark'
                                            ? 'bg-blue-900/40 text-blue-300'
                                            : 'bg-blue-50 text-blue-700'
                                        : theme === 'dark'
                                            ? 'text-gray-300 hover:bg-gray-700/70 hover:text-white'
                                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                }`}
                            >
                                <span className="flex-1">{sub.label}</span>
                                {sub.badgeKey && pendingBreakdown[sub.badgeKey] > 0 && (
                                    <span className="px-1.5 py-0.5 text-[10px] font-bold bg-red-600 text-white rounded-full leading-none mr-1">
                                        {pendingBreakdown[sub.badgeKey] > 99 ? '99+' : pendingBreakdown[sub.badgeKey]}
                                    </span>
                                )}
                                <ChevronRight className="w-3 h-3 flex-shrink-0 opacity-40" />
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    };

    const renderMenuItem = (item) => {
        const IconComponent = item.icon;
        const active = isActive(item.path);
        const isApprovals = item.path === '/manager/team-approvals';
        const hasSubItems = item.subItems && item.subItems.length > 0;
        const isHovered = hoveredItem === item.path;

        return (
            <div
                key={item.path}
                className="relative"
                onMouseEnter={(e) => hasSubItems && handleMouseEnter(item.path, e)}
                onMouseLeave={handleMouseLeave}
            >
                <NavLink
                    to={item.path}
                    className={`flex items-center space-x-2 px-2 py-1.5 rounded-md transition-colors group ${item.tourClass || ''} ${
                        active
                            ? theme === 'dark'
                                ? 'bg-blue-900 text-blue-100'
                                : 'bg-blue-50 text-blue-700'
                            : theme === 'dark'
                                ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                                : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                    title={isOpen ? '' : item.description}
                >
                    <div className="relative">
                        <IconComponent className="w-4 h-4 flex-shrink-0" />
                        {!isOpen && isApprovals && pendingCount > 0 && (
                            <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-3.5 px-0.5 text-[9px] font-bold bg-red-600 text-white rounded-full flex items-center justify-center">
                                {pendingCount > 99 ? '99+' : pendingCount}
                            </span>
                        )}
                    </div>
                    {isOpen && (
                        <>
                            <span className="text-sm flex-1 text-left">{item.label}</span>
                            {item.badge && (
                                <span className="px-1.5 py-0.5 text-xs bg-orange-500 text-white rounded">
                                    New
                                </span>
                            )}
                            {isApprovals && pendingCount > 0 && !hasSubItems && (
                                <span className="px-1.5 py-0.5 text-xs font-bold bg-red-600 text-white rounded-full">
                                    {pendingCount > 99 ? '99+' : pendingCount}
                                </span>
                            )}
                            {isApprovals && pendingCount > 0 && hasSubItems && (
                                <span className="px-1.5 py-0.5 text-xs font-bold bg-red-600 text-white rounded-full">
                                    {pendingCount > 99 ? '99+' : pendingCount}
                                </span>
                            )}
                            {hasSubItems && (
                                <ChevronRight className={`w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200 ${
                                    isHovered ? 'translate-x-0.5' : ''
                                } ${
                                    active
                                        ? theme === 'dark' ? 'text-blue-300' : 'text-blue-500'
                                        : theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                                }`} />
                            )}
                        </>
                    )}
                </NavLink>

                {/* Flyout sub-menu */}
                {renderFlyout(item)}
            </div>
        );
    };

    return (
        <aside className={`${isOpen ? 'w-64' : 'w-20'} h-screen border-r flex flex-col transition-all duration-300 ${
            theme === 'dark'
                ? 'bg-gray-800 border-gray-700'
                : 'bg-white border-gray-200'
        }`}>
            {/* Logo */}
            <div className={`p-3 border-b flex items-center justify-between ${
                theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}>
                {isOpen ? (
                    <div className="flex flex-col items-center justify-center flex-1">
                        <img src={logo} alt="Logo" className="h-10 w-auto object-contain" />
                        <p className={`mt-1 text-xs ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                            Powered by KONAV Technologies Pvt. Ltd.
                        </p>
                    </div>
                ) : (
                    <img src={logo2} alt="Logo" className="h-10 w-auto object-contain mx-auto" />
                )}
                <button
                    onClick={onToggle}
                    className={`p-1.5 rounded-lg transition-colors ${
                        theme === 'dark'
                            ? 'text-gray-300 hover:bg-gray-700'
                            : 'text-gray-600 hover:bg-gray-100'
                    }`}
                >
                    {isOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
            </div>

            {/* Navigation */}
            <nav className="flex-1 overflow-y-auto p-2 space-y-0.5">
                {/* Dashboard */}
                {(() => {
                    const IconComponent = dashboardItem.icon;
                    const active = isActive(dashboardItem.path);

                    return (
                        <NavLink
                            key={dashboardItem.path}
                            to={dashboardItem.path}
                            className={`flex items-center space-x-2 px-2 py-1.5 rounded-md transition-colors group ${dashboardItem.tourClass || ''} ${
                                active
                                    ? theme === 'dark'
                                        ? 'bg-blue-900 text-blue-100'
                                        : 'bg-blue-50 text-blue-700'
                                    : theme === 'dark'
                                        ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                            }`}
                            title={isOpen ? '' : dashboardItem.description}
                        >
                            <IconComponent className="w-4 h-4 flex-shrink-0" />
                            {isOpen && (
                                <span className="text-sm">{dashboardItem.label}</span>
                            )}
                        </NavLink>
                    );
                })()}

                {/* Divider after Dashboard */}
                <div className={`border-t my-2 ${
                    theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                }`} />

                {/* Personal Section */}
                {isOpen && (
                    <div className={`px-2 py-1 text-xs font-semibold uppercase tracking-wider ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                        Personal
                    </div>
                )}

                {personalMenuItems.map((item) => renderMenuItem(item))}

                {/* Team Management Section */}
                {isOpen && (
                    <div className={`px-2 py-1 mt-3 text-xs font-semibold uppercase tracking-wider ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                        Team Management
                    </div>
                )}

                {!isOpen && (
                    <div className={`border-t my-1 ${
                        theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                    }`} />
                )}

                {teamMenuItems.map((item) => renderMenuItem(item))}
            </nav>
        </aside>
    );
};

export default ManagerSidebar;
